import { PageScheduler, TPageProps, TPageSequence, useApiPlay } from "@ancademy/vse-client";
import { buildSubView, Em, IPlayProps, Md, showMsg, Style } from "@client";
import { css } from "@emotion/css";
import { NpcModel } from "@npc";
import { Choice, ISubPlayerState, SubNamespace } from "@share";
import { Button, Col, Modal, Row } from "antd";
import React, { useEffect } from "react";

namespace RiskAppetite {
  export const T = 9;

  export const Endowment = {
    A1: 1000,
    B1: 500,
    B2: 2000,
  };

  export function getP(i: number) {
    const p0 = 1,
      p1 = +((i + 1) / (T + 1)).toFixed(2),
      p2 = +((T - i) / (T + 1)).toFixed(2);
    return {
      p0,
      p1,
      p2,
    };
  }

  const fmtP = (p: number) => `${~~(p * 100)}%`;

  export function CaseRender({ i, onChoose }: { i: number; onChoose?: (choice: Choice) => void }) {
    const { p0, p1, p2 } = getP(i);
    return (
      <Row
        justify="space-around"
        className={css`
          padding: 1rem 0 2rem;
        `}
      >
        {[
          {
            choice: Choice.A,
            label: "选项A",
            className: Style.btnWarningSm,
            content: (
              <p>
                以<Em>{fmtP(p0)}</Em>的概率获得<Em>{Endowment.A1}</Em>点（实验币）的收益
              </p>
            ),
          },
          {
            choice: Choice.B,
            label: "选项B",
            className: Style.btnPrimarySm,
            content: (
              <>
                <p>
                  以<Em>{fmtP(p1)}</Em>的概率获得<Em>{Endowment.B1}</Em>点（实验币）的收益 ，
                </p>
                <p>
                  以<Em>{fmtP(p2)}</Em>的概率获得
                  <Em>{Endowment.B2}</Em>
                  点（实验币）的收益
                </p>
              </>
            ),
          },
        ].map(({ choice, label, className, content }) => (
          <Col
            span={12}
            className={css`
              display: flex;
              flex-direction: column;
              align-items: center;
            `}
          >
            <div
              className={css`
                font-size: 1.5rem;
                height: 10rem;
                display: flex;
                flex-direction: column;
                justify-content: center;
              `}
            >
              {content}
            </div>
            <Button className={onChoose ? className : Style.btnDisabledSm} onClick={() => onChoose?.(choice)}>
              {label}
            </Button>
          </Col>
        ))}
      </Row>
    );
  }

  export function RiskAppetite({ nextPage }: TPageProps) {
    const {
      apiState: { concept_riskAppetite },
      setApiState,
    } = useApiPlay<ISubPlayerState>();
    const i = concept_riskAppetite.length;
    return (
      <Modal key={i} {...Md.pageProps} footer={false}>
        <p className={Style.modalText}>
          为了理解搜寻的基本原理，请同学们先试想在以下A、B两种情况下，你将如何选择？({i + 1}/{T})
        </p>
        <CaseRender
          i={i}
          onChoose={(choice) =>
            setApiState((s) => {
              s.concept_riskAppetite[i] = choice;
              if (i >= T - 1) {
                nextPage();
              }
            })
          }
        />
      </Modal>
    );
  }
}

function Result({ nextPage }: TPageProps) {
  const {
    apiState: { concept_riskAppetite, concept_result },
    setApiState,
  } = useApiPlay<ISubPlayerState>();
  const { T, getP, Endowment, CaseRender } = RiskAppetite;
  useEffect(() => {
    if (concept_result) {
      return;
    }
    const i = ~~(Math.random() * T);
    const p = getP(i);
    const choice = concept_riskAppetite[i],
      endowment = choice === Choice.A ? Endowment.A1 : Math.random() > p.p1 ? Endowment.B1 : Endowment.B2;
    setApiState((s) => (s.concept_result = { i, choice, endowment }));
  }, []);
  if (!concept_result) {
    return null;
  }
  const { i, choice, endowment } = concept_result;
  return (
    <Modal {...Md.pageProps} cancelButtonProps={{ hidden: true }} onOk={nextPage}>
      <p className={Style.modalText}>
        系统为您随机到第{i + 1}种情况，您的选择为<Em>{choice === Choice.A ? "选项A" : "选项B"}</Em>:
      </p>
      <CaseRender i={i} />
      <p className={Style.modalText}>
        最终获得<Em>{endowment}</Em>点（实验币）的收益 。
      </p>
    </Modal>
  );
}

const pageSequence: TPageSequence = [RiskAppetite.RiskAppetite, Result];

function Play({ playDone }: IPlayProps) {
  const { game } = useApiPlay<ISubPlayerState>();
  return <PageScheduler gameId={game.id} schedulerKey="Play" pageSequence={pageSequence} onOver={playDone} />;
}

export const ConceptView = buildSubView({
  namespace: SubNamespace.Concept,
  npcModel: NpcModel.RentHouse,
  label: "理解市场搜寻中的权衡",
  Play,
  initState: (s) => (s.concept_riskAppetite = []),
  knowledgeMsg: async () => {
    await showMsg("想一想，为什么以上的练习与搜寻有关呢？");
    await showMsg("为了更好的理解搜寻市场，我们需要学习（复习）一些与搜寻市场有紧密联系的知识。", { okText: "好的" });
  },
});
